(() => {
  const TAG = 'reviews-carousel';

  class ReviewsCarousel extends HTMLElement {
    connectedCallback() {
      this.mask = this.querySelector('[data-reviews-mask]');
      this.track = this.querySelector('[data-reviews-track]');
      this.slides = [...this.querySelectorAll('[data-review-slide]')];
      this.prevBtn = this.querySelector('[data-reviews-prev]');
      this.nextBtn = this.querySelector('[data-reviews-next]');
      this.index = 0;

      this.autoplayEnabled = this.dataset.autoplay === 'true';
      const parsedMs = parseInt(this.dataset.autoplaySpeed, 10);
      this.autoplayMs = Number.isFinite(parsedMs) && parsedMs >= 2000 ? parsedMs : 5000;

      this._autoplayTimer = null;
      this._onVisibility = () => {
        if (document.hidden) {
          this._clearAutoplay();
        } else {
          this._scheduleAutoplay();
        }
      };

      this.prevBtn?.addEventListener('click', () => this.step(-1));
      this.nextBtn?.addEventListener('click', () => this.step(1));

      this._onResize = () => {
        this.clampIndex();
        this.updateTransform();
        this.updateNavState();
        this._scheduleAutoplay();
      };
      window.addEventListener('resize', this._onResize);

      this._ro = typeof ResizeObserver !== 'undefined' && this.mask
        ? new ResizeObserver(() => this._onResize())
        : null;
      this._ro?.observe(this.mask);

      this.addEventListener('mouseenter', () => this._clearAutoplay());
      this.addEventListener('mouseleave', () => this._scheduleAutoplay());
      this.addEventListener('focusin', () => this._clearAutoplay());
      this.addEventListener('focusout', () => {
        requestAnimationFrame(() => this._scheduleAutoplay());
      });

      document.addEventListener('visibilitychange', this._onVisibility);

      this.clampIndex();
      this.updateTransform();
      this.updateNavState();
      this._scheduleAutoplay();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._ro?.disconnect();
      this._clearAutoplay();
    }

    _clearAutoplay() {
      if (this._autoplayTimer != null) {
        clearInterval(this._autoplayTimer);
        this._autoplayTimer = null;
      }
    }

    _shouldAutoplay() {
      if (!this.autoplayEnabled) return false;
      if (document.hidden) return false;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
      if (this.matches(':hover')) return false;
      if (this.contains(document.activeElement)) return false;
      return this.slides.length > this.visibleCount();
    }

    _scheduleAutoplay() {
      this._clearAutoplay();
      if (!this._shouldAutoplay()) return;
      this._autoplayTimer = window.setInterval(() => {
        this.advanceWrap();
      }, this.autoplayMs);
    }

    visibleCount() {
      return window.matchMedia('(min-width: 990px)').matches
        ? Math.min(3, Math.max(1, this.slides.length))
        : 1;
    }

    maxIndex() {
      const v = this.visibleCount();
      return Math.max(0, this.slides.length - v);
    }

    clampIndex() {
      this.index = Math.min(this.maxIndex(), Math.max(0, this.index));
    }

    step(delta) {
      if (this.slides.length < 2) return;
      this.index = Math.min(this.maxIndex(), Math.max(0, this.index + delta));
      this.updateTransform();
      this.updateNavState();
      this._scheduleAutoplay();
    }

    advanceWrap() {
      if (this.slides.length < 2) return;
      const max = this.maxIndex();
      if (this.index >= max) {
        this.index = 0;
      } else {
        this.index += 1;
      }
      this.updateTransform();
      this.updateNavState();
    }

    gapPx() {
      if (!this.track) return 16;
      const g = getComputedStyle(this.track).gap;
      const n = parseFloat(g);
      return Number.isNaN(n) ? 16 : n;
    }

    updateTransform() {
      if (!this.track || !this.slides[0]) return;
      const slide = this.slides[0];
      const w = slide.offsetWidth;
      const offset = this.index * (w + this.gapPx());
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.style.transition = reduce ? 'none' : '';
      this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
    }

    updateNavState() {
      const max = this.maxIndex();
      if (this.prevBtn) {
        this.prevBtn.disabled = this.index <= 0;
        this.prevBtn.toggleAttribute('aria-disabled', this.index <= 0);
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = this.index >= max;
        this.nextBtn.toggleAttribute('aria-disabled', this.index >= max);
      }
    }
  }

  if (!customElements.get(TAG)) {
    customElements.define(TAG, ReviewsCarousel);
  }
})();
